import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronUp, ChevronDown } from "lucide-react";

import { ASSETS } from "@/config/brand";
import { PUBLIC_NAV } from "@/config/marketing";
import { Button } from "@/components/ui/button";

const SITE_URL = "https://residennce.com";

/** Lightweight per-page SEO: title, description, canonical, OG/Twitter. */
export function useSeo({ title, description, path = "/" }) {
  useEffect(() => {
    const full = title ? `${title} · RESIDENNCE` : "RESIDENNCE — Built for the Business of Real Estate";
    document.title = full;
    const set = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("meta"); document.head.appendChild(el); }
      const [a, v] = attr;
      el.setAttribute(a, v);
      el.setAttribute("content", val);
      return el;
    };
    if (description) {
      let d = document.head.querySelector('meta[name="description"]');
      if (d) d.setAttribute("content", description);
    }
    set('meta[property="og:title"]', ["property", "og:title"], full);
    if (description) set('meta[property="og:description"]', ["property", "og:description"], description);
    set('meta[property="og:type"]', ["property", "og:type"], "website");
    set('meta[property="og:url"]', ["property", "og:url"], SITE_URL + path);
    set('meta[name="twitter:title"]', ["name", "twitter:title"], full);
    if (description) set('meta[name="twitter:description"]', ["name", "twitter:description"], description);
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", SITE_URL + path);
  }, [title, description, path]);
}

export function TrialButton({ founding = false, className = "", size, children }) {
  const navigate = useNavigate();
  return (
    <Button size={size} className={className || "bg-brand-gold text-brand-navy hover:bg-brand-soft-gold"}
      data-testid={founding ? "cta-founding" : "cta-trial"}
      onClick={() => navigate(founding ? "/signup?plan=founding" : "/signup?plan=standard")}>
      {children || (founding ? "Become a Founding Agent" : "Start Your 14-Day Trial")}
      <ArrowRight className="h-4 w-4" />
    </Button>
  );
}

const FOOTER_COLS = [
  { title: "Product", links: [["Platform", "/platform"], ["Features", "/features"], ["RESA", "/resa"], ["Pricing", "/pricing"], ["How It Works", "/how-it-works"]] },
  { title: "Company", links: [["About", "/about"], ["Resources", "/resources"], ["Login", "/login"]] },
  { title: "Legal", links: [["Privacy", "/privacy"], ["Terms", "/terms"], ["Cancellation Policy", "/cancellation-policy"], ["Refund Policy", "/refund-policy"], ["Support", "/contact"]] },
];

/* Public marketing routes land at the top on navigation (footer/nav links). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, [pathname]);
  return null;
}

/* Tasteful floating half-page Up/Down controls for long public pages. */
function PublicScrollControls() {
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setAtTop(y <= 8);
      setAtBottom(y >= max - 8);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);
  const scrollBy = (dir) => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollBy({ top: dir * window.innerHeight * 0.5, behavior: reduce ? "auto" : "smooth" });
  };
  const btn = "grid h-10 w-10 place-items-center rounded-full border border-brand-gold/40 bg-brand-navy text-brand-gold shadow-lg transition-all hover:bg-brand-deep-blue hover:text-brand-soft-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold disabled:pointer-events-none disabled:opacity-0";
  return (
    <div className="fixed right-3 bottom-6 z-40 flex flex-col gap-2 sm:right-5" data-testid="public-scroll-controls">
      <button type="button" className={btn} onClick={() => scrollBy(-1)} disabled={atTop}
        aria-label="Scroll up" data-testid="scroll-up"><ChevronUp className="h-5 w-5" /></button>
      <button type="button" className={btn} onClick={() => scrollBy(1)} disabled={atBottom}
        aria-label="Scroll down" data-testid="scroll-down"><ChevronDown className="h-5 w-5" /></button>
    </div>
  );
}

export function MarketingLayout({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white text-brand-charcoal" data-testid="marketing-layout">
      <ScrollToTop />
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-5">
          <Link to="/" className="flex items-center" data-testid="marketing-logo">
            <img src={ASSETS.logoNavy} alt="RESIDENNCE" className="h-9 w-auto rounded" />
          </Link>
          <nav className="ml-4 hidden items-center gap-6 lg:flex" aria-label="Primary">
            {PUBLIC_NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `text-sm font-medium transition-colors hover:text-brand-navy ${isActive ? "text-brand-navy" : "text-charcoal/70"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto hidden items-center gap-3 lg:flex">
            <Link to="/login" className="text-sm font-medium text-brand-navy hover:underline" data-testid="nav-login">Login</Link>
            <TrialButton size="sm" className="bg-brand-navy text-white hover:bg-brand-deep-blue" />
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="marketing-menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {open && (
          <div className="border-t border-black/5 bg-white lg:hidden" data-testid="marketing-mobile-nav">
            <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-5 py-4" aria-label="Mobile">
              {PUBLIC_NAV.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className="rounded-lg px-2 py-2.5 text-base font-medium text-charcoal/80 hover:bg-brand-light-gray">{n.label}</NavLink>
              ))}
              <Link to="/login" onClick={() => setOpen(false)} className="rounded-lg px-2 py-2.5 text-base font-medium text-brand-navy">Login</Link>
              <div className="pt-2"><TrialButton className="w-full justify-center bg-brand-navy text-white hover:bg-brand-deep-blue" /></div>
            </nav>
          </div>
        )}
      </header>

      <main>{children}</main>

      {/* Footer */}
      <footer className="border-t border-black/5 bg-brand-navy text-white" data-testid="marketing-footer">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <img src={ASSETS.wordmark} alt="RESIDENNCE" className="h-7 w-auto opacity-90" />
            <p className="mt-4 max-w-xs text-sm text-white/60">The business operating system for real estate professionals.</p>
            <a href="mailto:support@residennce.com" className="mt-4 inline-block text-sm text-brand-soft-gold hover:underline">support@residennce.com</a>
          </div>
          {FOOTER_COLS.map((c) => (
            <div key={c.title}>
              <p className="text-xs font-bold uppercase tracking-widest text-white/40">{c.title}</p>
              <ul className="mt-4 space-y-2.5">
                {c.links.map(([l, to]) => (
                  <li key={l}><Link to={to} className="text-sm text-white/70 transition-colors hover:text-white">{l}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10">
          <div className="mx-auto max-w-6xl px-5 py-5 text-xs text-white/50">
            © {new Date().getFullYear()} RESIDENNCE. AgentHQ is a product of RESIDENNCE. RESA is the Real Estate Strategic Assistant built into AgentHQ.
          </div>
        </div>
      </footer>
      <PublicScrollControls />
    </div>
  );
}

export const Section = ({ children, className = "", ...rest }) => (
  <section className={`mx-auto max-w-6xl px-5 ${className}`} {...rest}>{children}</section>
);

export const Eyebrow = ({ children }) => (
  <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold">{children}</span>
);
